import { useEffect, useState } from "react";
import { Col, Container, FormCheck, Row } from "react-bootstrap";
import { useAppDispatch, useAppSelector } from "../../app/store";
import { getApplicants } from "../../features/applicantSlice";
import moment from "moment";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { updateJobStatus } from "../../features/jobSlice";
import { PuffLoader } from "react-spinners";

const ManageApplicants = () => {
  const dispatch = useAppDispatch();
  const { applicants, isLoading } = useAppSelector((state) => state.applicant);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [bulkStatus, setBulkStatus] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const { id } = useParams();
  const validJobId = id ? Number(id) : 0;

  useEffect(() => {
    if (validJobId) {
      dispatch(getApplicants(validJobId));
    }
  }, [dispatch, validJobId]);

  const handleCheckboxChange = (id: number) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((val) => val !== id) : [...prev, id]
    );
  };

  const handleBulkUpdate = async (status: string) => {
    const data = {
      jobIds: selectedIds,
      jobStatus: status,
    };

    dispatch(updateJobStatus(data))
      .unwrap()
      .then(() => {
        dispatch(getApplicants(validJobId));
        toast.success("Job status updated successfully.");
        setSelectedIds([]);
        setBulkStatus("");
      })
      .catch((error) => {
        toast.error(error);
      });
  };

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedValue = e.target.value;

    if (selectedIds.length === 0) {
      toast.warning("Select applicants first.");
      return;
    }

    setBulkStatus(selectedValue);
    handleBulkUpdate(selectedValue);
  };

  if (!applicants) {
    return (
      <div className="flex d-flex justify-content-center align-item center py-5 mt-5">
        <PuffLoader
          color="#073367"
          loading={isLoading}
          size={150}
          aria-label="Loading Spinner"
          data-testid="loader"
        />
      </div>
    );
  }

  const filteredApplicants =
    applicants?.data?.length && applicants.data.length > 0
      ? applicants.data.filter(
          (item: any) =>
            item.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.status.toLowerCase().includes(searchTerm.toLowerCase())
        )
      : [];

  return (
    <Container fluid>
      <Row className="py-5">
        <Col md={6}>
          <div className="form-group mx-2">
            <select
              className="form-select w-25"
              value={bulkStatus}
              onChange={handleChange}
            >
              <option value="">Bulk Action</option>
              <option value="shortlisted">Shortlist Applicant</option>
              <option value="rejected">Reject Applicant</option>
            </select>
          </div>
        </Col>
        <Col md={6} sm={12}>
          <div className="d-flex flex-row justify-content-end">
            <div className="form-group mx-2">
              <select
                className="form-select"
                onChange={(e) => setSearchTerm(e.target.value)}
              >
                <option value="">Filter By: All Status</option>
                <option value="shortlisted">Shortlisted</option>
                <option value="interview">Interviewing</option>
                <option value="hired">Hired</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
            <div className="form-group has-search">
              <input
                type="text"
                className="form-control"
                placeholder="Search"
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </Col>
      </Row>

      <div className="applicant-table table-responsive">
        <table className="table table-hover table-strip border-top">
          <thead>
            <tr>
              <th className="py-3">
                <FormCheck
                  checked={
                    filteredApplicants.length > 0 &&
                    filteredApplicants.every((applicant) =>
                      selectedIds.includes(applicant.id)
                    )
                  }
                  onChange={(e) => {
                    if (e.target.checked) {
                      const filteredIds = filteredApplicants.map((a) => a.id);
                      setSelectedIds((prev) => [
                        ...Array.from(new Set([...prev, ...filteredIds])),
                      ]);
                    } else {
                      setSelectedIds((prev) =>
                        prev.filter(
                          (id) =>
                            !filteredApplicants.some((app) => app.id === id)
                        )
                      );
                    }
                  }}
                />
              </th>
              <th>CANDIDATE NAME</th>
              <th>EMAIL</th>
              <th>QUALITY RATE</th>
              <th>RESUME</th>
              <th>STATUS</th>
              <th>DATE APPLIED</th>
              <th>ACTION</th>
            </tr>
          </thead>
          <tbody>
            {filteredApplicants.length > 0 ? (
              filteredApplicants.map((item) => (
                <tr key={item.id}>
                  <td className="py-3">
                    <FormCheck
                      checked={selectedIds.includes(item.id)}
                      onChange={() => handleCheckboxChange(item.id)}
                    />
                  </td>
                  <td className="py-3">
                    {item.first_name} {item.last_name}
                  </td>
                  <td className="py-3">{item.email}</td>
                  <td className="py-3">
                    {item.totalMark > 100
                      ? 100
                      : item.totalMark
                      ? Number(item.totalMark).toFixed(2)
                      : "0.00"}{" "}
                    %
                  </td>
                  <td>
                    <a href={item.resume_url}>
                      <i className="bi bi-download fs-4 text-primary"></i>
                    </a>
                  </td>
                  <td className="py-3">
                    <span
                      className={
                        item.status === "hired"
                          ? "bg-green text-success px-3 py-2"
                          : item.status === "awaiting"
                          ? "text-awaiting p-3 py-2"
                          : item.status === "rejected"
                          ? "text-rejected px-3 py-2"
                          : item.status === "interview"
                          ? "text-interview px-3 py-2"
                          : item.status === "shortlisted"
                          ? "text-shortlisted px-3 py-2"
                          : "bg-secondary text-primary p-1"
                      }
                    >
                      {item.status}
                    </span>
                  </td>
                  <td className="py-3">
                    {moment(item.applied_date).format("YYYY-MM-DD")}
                  </td>
                  <td className="py-3">
                    <a
                      className="dropdown-toggle p-2 text-decoration-none text-muted"
                      href="#"
                      id="dropdownMenuLink"
                      data-bs-toggle="dropdown"
                      aria-expanded="false"
                    >
                      <i className="bi bi-three-dots"></i>
                    </a>
                    <ul
                      className="dropdown-menu"
                      aria-labelledby="dropdownMenuLink"
                    >
                      <li>
                        <a
                          className="dropdown-item"
                          href={`/manageJob/manage-applicants/${item.job_id}/profile/${item.user_id}`}
                        >
                          View Profile
                        </a>
                      </li>
                      <li>
                        <a className="dropdown-item" href="#">
                          Schedule Interview
                        </a>
                      </li>
                      <li>
                        <a className="dropdown-item" href="#">
                          Conduct Interview
                        </a>
                      </li>
                    </ul>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={8} className="text-center">
                  No application yet
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </Container>
  );
};

export default ManageApplicants;
